// What the reader may do with a node. Node reads carry `access.can` (read,
// comment, create, edit, share, download, rename, move, delete, visibility,
// settings) — the backend's one rule; a read that doesn't (older lists, raw
// fixtures) falls back to the old reading: the owner does everything, a
// `can_edit` reader edits, everyone who sees it reads, comments and
// downloads.

import { getAccessToken } from './embedTokenManager.js';

const OWNER_ONLY = new Set(['share', 'rename', 'move', 'delete', 'visibility', 'settings', 'create']);

export function isOwner(node) {
  const owner = node?.ownerId ?? node?.owner_id;
  const me = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
  return owner != null && me != null && String(owner) === String(me);
}

export function can(node, ability) {
  if (!node) return false;
  const list = node.access?.can;
  if (Array.isArray(list)) return list.includes(ability);
  if (isOwner(node)) return true;
  if (ability === 'edit') return Boolean(node.canEdit ?? node.can_edit);
  return !OWNER_ONLY.has(ability);
}

// The scopes this session's token carries — read for what to offer only; the
// backend checks them again.
export function tokenScopes() {
  try {
    const payload = getAccessToken().split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const scopes = JSON.parse(atob(payload)).scopes;
    return Array.isArray(scopes) ? scopes : [];
  } catch {
    return [];
  }
}

// Who may hand a node over to someone else: its owner, or a console manager
// of the project (how a project takes back what someone who left made).
export function canHandOver(node) {
  if (!node) return false;
  return node.access?.level === 'owner' || isOwner(node) || tokenScopes().includes('console:manage');
}

// ── Editing a file ────────────────────────────────────────────────────
// File reads carry `edit_block` — why nobody may change the bytes right now
// ('locked' | 'signed' | 'agreement' | 'record', or null) — and a `can_edit`
// that already accounts for it. A read from an older backend has neither
// rule; it is derived from the fields that read does carry.

const LEVELS = ['viewer', 'commenter', 'contributor', 'editor', 'manager', 'owner'];

const hasEditBlock = (node) => Boolean(node) && ('editBlock' in node || 'edit_block' in node);

/** The reader's access level is `floor` or above. */
export function atLeast(node, floor) {
  if (!node) return false;
  const level = node.access?.level;
  if (level && LEVELS.includes(level)) return LEVELS.indexOf(level) >= LEVELS.indexOf(floor);
  if (isOwner(node)) return true;
  if (LEVELS.indexOf(floor) <= LEVELS.indexOf('editor')) {
    return Boolean(node.canEdit ?? node.can_edit);
  }
  return false;
}

/** The file's lock ({byId, byName, at, reason, ref}), or null. */
export function lockOf(node) {
  const lock = node?.lock;
  if (!lock || typeof lock !== 'object') return null;
  return {
    byId: lock.byId ?? lock.by_id ?? null,
    byName: lock.byName ?? lock.by_name ?? null,
    at: lock.at ?? null,
    reason: lock.reason ?? '',
    ref: lock.ref ?? null,
  };
}

export function isLockedByMe(node) {
  const lock = lockOf(node);
  const me = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
  return Boolean(lock) && lock.byId != null && me != null && String(lock.byId) === String(me);
}

/** Why the file can't be edited now: 'locked' | 'signed' | 'agreement' | 'record' | null. */
export function editBlock(node) {
  if (!node) return null;
  if (hasEditBlock(node)) return (node.editBlock ?? node.edit_block) || null;
  if (lockOf(node)) return 'locked';
  const state = node.approvalState || node.approval_state;
  if (state === 'signed' || state === 'agreement') return state;
  if (node.filedAt || node.filed_at) return 'record';
  return null;
}

/** Whether the reader may open the file in the editor now. */
export function canEditFile(node) {
  if (!node || editBlock(node)) return false;
  if (Array.isArray(node.access?.can)) return node.access.can.includes('edit');
  const flag = node.canEdit ?? node.can_edit;
  if (flag != null) return Boolean(flag);
  return isOwner(node);
}

const isFolderNode = (node) =>
  node?.type === 'folder' || node?.nodeType === 'folder' || node?.node_type === 'folder';

/**
 * Lock… in a file's menu: what `PUT /file/{id}/lock` accepts — an editor on
 * an ordinary file nobody has locked. Offered only when reads carry `lock`
 * (a backend that has file locks).
 */
export function canLockFile(node) {
  if (!node || isFolderNode(node) || !('lock' in node)) return false;
  if (node.archivedAt || node.archived_at) return false;
  return !lockOf(node) && !editBlock(node) && atLeast(node, 'editor');
}

/** Unlock: whoever locked it, a manager or the owner, or a console manager. */
export function canUnlockFile(node) {
  if (!lockOf(node)) return false;
  return isLockedByMe(node)
    || can(node, 'share')
    || atLeast(node, 'manager')
    || tokenScopes().includes('console:manage');
}

/** The current version ({number, sha256, size, createdAt, createdById, sealed}), or null. */
export function versionOf(node) {
  const version = node?.version;
  if (!version || typeof version !== 'object') return null;
  return {
    number: version.number ?? null,
    sha256: version.sha256 ?? null,
    size: version.size ?? null,
    createdAt: version.createdAt ?? version.created_at ?? null,
    createdById: version.createdById ?? version.created_by_id ?? null,
    sealed: Boolean(version.sealed),
  };
}
